/*
 * Copyright (c) 2020. American Express Company
 */

import * as _ from 'lodash';
import Pagination from '@mui/lab/Pagination';
import React, { ReactComponentElement } from 'react';

// models and services
import { CategoryModel } from 'src/types/category.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { TransactionModel } from 'src/types/transaction.model';
import CategoryService from '../../../../services/category.service';
import TransactionService, { Frequency } from '../../../../services/transaction.service';
import IncomeSourceService from '../../../../services/income-source.service';
import NavigationService from '../../../../services/navigation.service';
import SearchService from '../../../../services/search.service';

// components
import BasicDrawer from 'src/bw/bw-drawer/common/BasicDrawer';
import { ButtonTertiary, IconCancelCircle, IconSearch, Input } from '@americanexpress/dls-react';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import Typography from '../../../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import UnifiedTransactionsTable from './UnifiedTransactionsTable';
import WideView from 'src/components/atomic/layouts/wideView/WideView';

// icons and css
import './UnifiedReviewTransactionsDrawer.scss';
import colors from 'src/styles/colors.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';

export default class UnifiedReviewTransactionsDrawer extends BasicDrawer<
    any,
    {
        search: string;
        showButtonBar: boolean;
        changeToGroupUid: string;
        changeToIncomeUid: string;
        changeToFrequency: string;
        moveAll: boolean;
        // new category
        showNewCategory: boolean;
        showNewIncomeSource: boolean;
        newCategory: string;
        newCategoryParentName: string;
        newCategoryParentId: string;
    }
> {
    public override state = {
        search: '',
        showButtonBar: false,
        changeToGroupUid: null as string,
        changeToIncomeUid: null as string,
        changeToFrequency: null as string,
        moveAll: false,
        // new category
        showNewCategory: false,
        showNewIncomeSource: false,
        newCategory: '' as string,
        newCategoryParentName: '' as string,
        newCategoryParentId: '' as string
    };

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;

        const addCategoryDiv =
            this.state.showNewCategory || this.state.showNewIncomeSource ? (
                <div className="new-category-dialog" key="new-category-dialog">
                    <Typography
                        variant={TypographyVariants.HEADING_M}
                        key="new-category-text"
                        className="new-category-text after16"
                    >
                        {this.state.showNewCategory
                            ? `Add new category to ${this.state.newCategoryParentName}`
                            : 'Add new income source'}
                    </Typography>
                    <Input
                        key="new-category"
                        id="new-category"
                        type="string"
                        className="new-category after16"
                        value={this.state.newCategory}
                        variant="outlined"
                        onChange={(e: any) => {
                            this.setState({ newCategory: e.target.value });
                        }}
                    />
                    <ButtonTertiary
                        id="save"
                        key="save-category"
                        onClick={async () => {
                            await this.newCategory();
                        }}
                    >
                        {this.state.showNewCategory ? 'Add subcategory' : 'Add income source'}
                    </ButtonTertiary>
                    <ButtonTertiary
                        borderless={true}
                        id="save"
                        key="cancel-category"
                        onClick={() => {
                            this.setState({
                                showNewCategory: false,
                                showNewIncomeSource: false
                            });
                        }}
                    >
                        Cancel
                    </ButtonTertiary>
                </div>
            ) : null;

        const buttonBar = this.state.showButtonBar ? (
            <div className="button-bar" key="button-bar">
                <Typography variant={TypographyVariants.SUBTEXT}>
                    {drawerProps.selectedTransactions.length} selected
                </Typography>
                <ButtonTertiary
                    size="sm"
                    onClick={async () => {
                        this.setState({
                            showButtonBar: false
                        });
                        await this.approveSelectedExpenses();
                    }}
                    key="frequency"
                    id="add-asset"
                >
                    Approve
                </ButtonTertiary>
                <ClickableIcon
                    className="close"
                    icon={<IconCancelCircle />}
                    onClick={() => {
                        this.setState({ showButtonBar: false });
                    }}
                />
            </div>
        ) : null;

        return (
            <WideView
                classNameView="unified-review-transactions-drawer"
                titleElement={
                    <Typography variant={TypographyVariants.HEADING_S} className={`transactions-num ${colors.brand}`}>
                        {LiteralsService.getLiteral('UnifiedReviewTransactionsDrawer.title', [
                            drawerProps.filteredTransactions?.length || 0
                        ])}
                    </Typography>
                }
                showFooter
                history={this.props.drawerProps.history}
                handleBack={this.props.handleBack}
            >
                {addCategoryDiv}
                {buttonBar}
                <div className="header-div">
                    <div className="header-row">
                        <ButtonTertiary
                            onClick={async () =>
                                await NavigationService.goTo(
                                    this.props.history,
                                    '/drawer/rules',
                                    'Expense rules drawer'
                                )
                            }
                            key="investment"
                            id="add-asset"
                        >
                            Manage rules
                        </ButtonTertiary>
                    </div>
                    <div className="header-row">
                        <div>
                            <Typography variant={TypographyVariants.SUBTEXT}>
                                {LiteralsService.getLiteral('UnifiedReviewTransactionsDrawer.subtitle')}
                            </Typography>
                        </div>
                        <div className="search">
                            <Input
                                type="string"
                                placeholder="Search"
                                value={this.state.search || ''}
                                variant="outlined"
                                onChange={(e: any) => this.handleChange('search', e.target.value)}
                                onKeyUp={async (e: any) => this.handleKeyUp(e)}
                                inputProps={{
                                    maxLength: 32
                                }}
                                InputProps={{
                                    startAdornment: <IconSearch className="marginRight" />
                                }}
                            />
                            {drawerProps.search?.length ? (
                                <ClickableIcon
                                    icon={<IconCancelCircle className="close" />}
                                    onClick={() => {
                                        this.setState({ search: '' });
                                        this.handleUpdate('search', '');
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className="clear" />

                <UnifiedTransactionsTable
                    key="transactions-table"
                    drawerProps={drawerProps}
                    showHidden={drawerProps.showHidden}
                    saveLabel={'Save'}
                    handleBack={this.props.handleBack}
                    handleChangeIncomeSource={this.props.transactionFactory.handleChangeIncomeSource.bind(
                        this.props.transactionFactory
                    )}
                    handleChangeOccurrence={this.handleChangeOccurrence.bind(this)}
                    handleDisplayTransaction={this.props.transactionFactory.handleDisplayTransaction.bind(
                        this.props.transactionFactory
                    )}
                    handleIgnoreFutureTransaction={this.props.transactionFactory.handleIgnoreFutureTransaction.bind(
                        this.props.transactionFactory
                    )}
                    handleSortUnifiedTransaction={this.props.transactionFactory.handleSortUnifiedTransaction.bind(
                        this.props.transactionFactory
                    )}
                    handleChangeTransactionGroup={this.props.transactionFactory.handleChangeTransactionGroup.bind(
                        this.props.transactionFactory
                    )}
                    selectAllTransactions={this.selectAllTransactions.bind(this)}
                    selectTransaction={this.selectTransaction.bind(this)}
                    selectedTransactions={drawerProps.selectedTransactions || []}
                    transactions={drawerProps.displayedTransactions}
                    addNewIncomeSource={this.addNewIncomeSource.bind(this)}
                    isReview={true}
                    updateDrawer={this.props.updateDrawer}
                    user={this.props.user}
                    handleDeleteIncomeRule={this.props.handleDeleteIncomeRule}
                    handleDeleteRule={this.props.handleDeleteRule}
                    approveTransaction={this.approveTransaction.bind(this)}
                    updateFilteredTransactions={this.props.transactionFactory.updateFilteredTransactions.bind(
                        this.props.transactionFactory
                    )}
                    review={true}
                />
                {drawerProps.filteredTransactions.length > drawerProps.pageSize ? (
                    <Pagination
                        count={Math.ceil(drawerProps.filteredTransactions.length / drawerProps.pageSize)}
                        page={drawerProps.page}
                        onChange={(e: any, page: number) => {
                            this.handleUpdate('page', page);
                        }}
                        variant="outlined"
                        shape="rounded"
                        className="pagination"
                    />
                ) : null}
            </WideView>
        );
    }

    private handleChange(field: string, value: any): void {
        const state: any = { ...this.state };
        state[field] = value;
        this.setState(state);
    }

    private handleUpdate(field: string, value: any): void {
        let drawerProps: any = { ...this.props.drawerProps };
        drawerProps[field] = value;

        switch (field) {
            case 'account':
                NavigationService.goTo(
                    this.props.history,
                    SearchService.getUriWithParam(window.location.href, {
                        account: value,
                        page: 1
                    })
                ).catch(console.error);
                break;
            case 'groupUid':
                NavigationService.goTo(
                    this.props.history,
                    SearchService.getUriWithParam(window.location.href, {
                        group: value,
                        page: 1
                    })
                ).catch(console.error);
                break;
            case 'incomeSource':
                NavigationService.goTo(
                    this.props.history,
                    SearchService.getUriWithParam(window.location.href, {
                        incomesource: value,
                        page: 1
                    })
                ).catch(console.error);
                break;
            case 'page':
                NavigationService.goTo(
                    this.props.history,
                    SearchService.getUriWithParam(window.location.href, { page: value })
                ).catch(console.error);
                break;
            case 'search':
                NavigationService.goTo(
                    this.props.history,
                    SearchService.getUriWithParam(window.location.href, {
                        search: value,
                        page: 1
                    })
                ).catch(console.error);
                break;
            case 'type':
                NavigationService.goTo(
                    this.props.history,
                    SearchService.getUriWithParam(window.location.href, {
                        type: value,
                        page: 1
                    })
                ).catch(console.error);
                break;
        }

        drawerProps = this.props.transactionFactory.updateFilteredTransactions(drawerProps);

        this.props.updateDrawer(drawerProps);
    }

    private handleKeyUp(event: any): void {
        if (event.keyCode === 13) {
            this.handleUpdate('search', this.state.search);
        }
    }

    private selectTransaction(transaction: TransactionModel, checked: boolean): void {
        let selectedTransactions: TransactionModel[] = this.props.drawerProps.selectedTransactions || [];
        let showButtonBar = this.state.showButtonBar;

        if (checked) {
            selectedTransactions.push(transaction);
            showButtonBar = true;
        } else {
            selectedTransactions = _.filter(selectedTransactions, (aTransaction) => aTransaction.id !== transaction.id);
            if (!selectedTransactions.length) {
                showButtonBar = false;
            }
        }

        this.setState({
            showButtonBar
        });

        this.props.updateDrawer({
            ...this.props.drawerProps,
            selectedTransactions
        });
    }

    private selectAllTransactions(checked: boolean): void {
        let selectedTransactions: TransactionModel[] = [];
        let showButtonBar;

        if (checked) {
            selectedTransactions = this.props.drawerProps.displayedTransactions;
            showButtonBar = true;
        } else {
            showButtonBar = false;
        }

        this.setState({
            showButtonBar
        });

        this.props.updateDrawer({
            ...this.props.drawerProps,
            selectedTransactions
        });
    }

    private async newCategory(): Promise<void> {
        if (this.state.showNewCategory) {
            await CategoryService.createCategory({
                name: this.state.newCategory,
                parentId: this.state.newCategoryParentId
            } as CategoryModel);

            const categories: CategoryModel[] = await CategoryService.getCategories();

            this.props.updateDrawer({
                ...this.props.drawerProps,
                categories
            });
        } else {
            const newIncome: IncomeSourceModel = new IncomeSourceModel();
            newIncome.name = this.state.newCategory;
            await IncomeSourceService.createIncomeSource(newIncome);

            const incomeSources: IncomeSourceModel[] = _.sortBy(
                await IncomeSourceService.getIncomeSources(),
                (i) => i.name
            );

            this.props.updateDrawer({
                ...this.props.drawerProps,
                incomeSources
            });
        }

        this.setState({
            newCategory: '',
            showNewCategory: false,
            showNewIncomeSource: false
        });
    }

    private addNewIncomeSource(): void {
        this.setState({ showNewIncomeSource: true });
    }

    private async handleChangeOccurrence(transaction: TransactionModel, occurrence: string): Promise<void> {
        switch (occurrence) {
            case Frequency.TYPICAL:
                if (transaction.ignoreInFuture) {
                    await this.props.transactionFactory.handleIgnoreFutureTransaction(transaction);
                }
                if (transaction.ignore) {
                    await this.props.transactionFactory.handleDisplayTransaction(transaction);
                }
                break;

            case Frequency.ONETIME:
                if (!transaction.ignoreInFuture) {
                    await this.props.transactionFactory.handleIgnoreFutureTransaction(transaction);
                }
                if (transaction.ignore) {
                    await this.props.transactionFactory.handleDisplayTransaction(transaction);
                }
                break;

            case Frequency.IGNORE:
                if (!transaction.ignore) {
                    await this.props.transactionFactory.handleDisplayTransaction(transaction);
                }
                break;
        }
    }

    private async approveTransaction(transaction: TransactionModel): Promise<void> {
        await TransactionService.markTransactionReviewed(transaction.id); // don't wait

        let drawerProps = this.props.drawerProps;
        const transactions: TransactionModel[] = this.props.drawerProps.transactions;
        drawerProps.transactions = _.pull(transactions, transaction);
        drawerProps = this.props.transactionFactory.updateFilteredTransactions(drawerProps);

        this.props.updateDrawer(drawerProps);
    }

    private async approveSelectedExpenses(): Promise<void> {
        let drawerProps = this.props.drawerProps;
        let transactions: TransactionModel[] = this.props.drawerProps.transactions;

        for (const transaction of this.props.drawerProps.selectedTransactions) {
            await TransactionService.markTransactionReviewed(transaction); // don't wait
            transactions = _.pull(transactions, transaction);
        }

        drawerProps.transactions = transactions;
        drawerProps = this.props.transactionFactory.updateFilteredTransactions(drawerProps);
        this.props.updateDrawer(drawerProps);
    }
}
